import type { Rec } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fnum, fpct } from "../lib/format.js";
import { COMP_ORDER, COMP_LABEL, compRows } from "../lib/comps.js";

/** Per-component roll-up used by the Overview and Components panels. */
export function ComponentTable({ rows, title }: { rows: Rec[]; title: string }) {
  const totals = computeStats(rows);
  const lines = COMP_ORDER
    .map((c) => ({ c, cr: compRows(rows, c) }))
    .filter((x) => x.cr.length > 0)
    .map((x) => ({ c: x.c, s: computeStats(x.cr) }));

  return (
    <div className="sec">
      <div className="card">
        <div className="card-head">
          <h3><i className="bi bi-grid-3x3-gap hico" aria-hidden="true" />{title}</h3>
          <span className="sub">Percentages are computed from the records in scope</span>
        </div>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Component</th>
                <th className="n">Records</th>
                <th className="n">CR received</th>
                <th className="n">%</th>
                <th className="n">Priced</th>
                <th className="n">% of recv</th>
                <th className="n">Awaiting</th>
                <th className="n">Not received</th>
                <th className="n">Estimated</th>
                <th className="n">% of n-recv</th>
                <th className="n">Est. outstanding</th>
                <th className="n">Crosschecked</th>
                <th className="n">Exported</th>
                <th className="n">Complete</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(({ c, s }) => (
                <tr key={c}>
                  <td className="strong">{COMP_LABEL[c]}</td>
                  <td className="n">{fnum(s.total)}</td>
                  <td className="n">{fnum(s.received)}</td>
                  <td className="n">{fpct(s.received, s.total)}</td>
                  <td className="n">{fnum(s.priced)}</td>
                  <td className="n">{fpct(s.received - s.awaiting, s.received)}</td>
                  <td className="n">{fnum(s.awaiting)}</td>
                  <td className="n">{fnum(s.notReceived)}</td>
                  <td className="n">{fnum(s.estimated)}</td>
                  <td className="n">{fpct(s.notReceived - s.estOutstanding, s.notReceived)}</td>
                  <td className="n">{fnum(s.estOutstanding)}</td>
                  <td className="n">{fnum(s.crosschecked)}</td>
                  <td className="n">{fnum(s.exported)}</td>
                  <td className="n strong">{s.completion.toFixed(1)}%</td>
                </tr>
              ))}
              <tr className="tfoot-row">
                <td>Total</td>
                <td className="n">{fnum(totals.total)}</td>
                <td className="n">{fnum(totals.received)}</td>
                <td className="n">{fpct(totals.received, totals.total)}</td>
                <td className="n">{fnum(totals.priced)}</td>
                <td className="n">{fpct(totals.received - totals.awaiting, totals.received)}</td>
                <td className="n">{fnum(totals.awaiting)}</td>
                <td className="n">{fnum(totals.notReceived)}</td>
                <td className="n">{fnum(totals.estimated)}</td>
                <td className="n">{fpct(totals.notReceived - totals.estOutstanding, totals.notReceived)}</td>
                <td className="n">{fnum(totals.estOutstanding)}</td>
                <td className="n">{fnum(totals.crosschecked)}</td>
                <td className="n">{fnum(totals.exported)}</td>
                <td className="n">{totals.completion.toFixed(1)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="tblnote">
          <b>% of recv</b> is the share of <i>received</i> CRs that have been priced, and{" "}
          <b>% of n-recv</b> the share of <i>not-received</i> CRs that have been estimated.
          Counts in the &ldquo;Priced&rdquo; and &ldquo;Estimated&rdquo; columns are every record
          with that status &mdash; including a handful entered against the opposite CR state,
          which is why a count can sit above its own base. Those records are listed on the
          Crosscheck tab.
        </div>
      </div>
    </div>
  );
}
